import { useState } from 'react';
import {
  Box, Card, CardContent, Typography, Stack, Chip, Grid, alpha, Button, Table, TableBody,
  TableCell, TableHead, TableRow, Dialog, DialogTitle, DialogContent, DialogActions, IconButton, Tooltip,
} from '@mui/material';
import { ArrowForward, HelpOutline, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { resolveServiceIcon } from '../../constants/serviceIcons';
import {
  DELIVERY_PARTNER_FLOW_META,
  FOOD_DELIVERY_PARTNER_FLOW,
  MULTI_SERVICE_DELIVERY_TYPES,
  DELIVERY_PARTNER_FEATURES,
  DELIVERY_EARNINGS_STRUCTURE,
  type FlowStep,
} from '../../constants/deliveryPartnerFlow';

function FlowPipeline({ steps, accent }: { steps: FlowStep[]; accent: string }) {
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
      {steps.map((step, i) => (
        <Stack key={step.label} direction="row" alignItems="center" spacing={0.5}>
          <Chip
            icon={<Box sx={{ display: 'flex', pl: 0.5 }}>{resolveServiceIcon(step.icon)}</Box>}
            label={step.label}
            size="small"
            sx={{
              height: 'auto',
              py: 0.5,
              '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.2 },
              bgcolor: alpha(accent, 0.08),
              border: 1,
              borderColor: alpha(accent, 0.2),
            }}
          />
          {i < steps.length - 1 && <ArrowForward sx={{ fontSize: 14, color: 'text.disabled' }} />}
        </Stack>
      ))}
    </Box>
  );
}

function GuideBody({ compact, onNavigate }: { compact?: boolean; onNavigate: (path: string) => void }) {
  return (
    <>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {DELIVERY_PARTNER_FLOW_META.subtitle}
      </Typography>

      <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
        Delivery Partner Flow
      </Typography>
      <FlowPipeline steps={FOOD_DELIVERY_PARTNER_FLOW} accent="#ea580c" />

      {!compact && (
        <>
          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Multi-Service (Same Partner)
          </Typography>
          <Grid container spacing={1}>
            {MULTI_SERVICE_DELIVERY_TYPES.map((row) => (
              <Grid item xs={12} md={4} key={row.slug}>
                <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: 'action.hover', height: '100%' }}>
                  <Typography variant="body2" fontWeight={700}>{row.service}</Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    Pickup: {row.pickup}
                  </Typography>
                  <Chip label={row.otp} size="small" sx={{ mt: 1 }} variant="outlined" />
                </Box>
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Delivery Partner Features
          </Typography>
          <Grid container spacing={1}>
            {DELIVERY_PARTNER_FEATURES.map((fn) => (
              <Grid item xs={12} sm={6} key={fn.step}>
                {fn.path ? (
                  <Button
                    fullWidth
                    variant="text"
                    size="small"
                    onClick={() => onNavigate(fn.path!)}
                    sx={{ justifyContent: 'flex-start', textAlign: 'left', py: 0.75, px: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}
                  >
                    <Typography variant="body2" component="span">
                      <Box component="span" fontWeight={700} color="primary.main" sx={{ mr: 1 }}>{fn.step}.</Box>
                      {fn.label}
                    </Typography>
                  </Button>
                ) : (
                  <Box sx={{ py: 0.75, px: 1.5, borderRadius: 1, bgcolor: 'action.hover' }}>
                    <Typography variant="body2">
                      <Box component="span" fontWeight={700} color="primary.main" sx={{ mr: 1 }}>{fn.step}.</Box>
                      {fn.label}
                    </Typography>
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>

          <Typography variant="subtitle2" fontWeight={700} sx={{ mt: 2.5, mb: 1 }}>
            Earnings Structure
          </Typography>
          <Box sx={{ overflowX: 'auto', border: 1, borderColor: 'divider', borderRadius: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: 'action.hover' }}>
                  <TableCell sx={{ fontWeight: 700 }}>Component</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {DELIVERY_EARNINGS_STRUCTURE.map((row) => (
                  <TableRow key={row.component} hover>
                    <TableCell sx={{ fontSize: '0.8125rem', fontWeight: 600 }}>{row.component}</TableCell>
                    <TableCell sx={{ fontSize: '0.8125rem', color: 'text.secondary' }}>{row.type}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        </>
      )}
    </>
  );
}

interface DeliveryPartnerFlowGuideProps {
  compact?: boolean;
  /** When true, render only a help icon that opens the notes in a dialog. */
  asHelpIcon?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

export default function DeliveryPartnerFlowGuide({
  compact,
  asHelpIcon,
  open: controlledOpen,
  onOpenChange,
}: DeliveryPartnerFlowGuideProps) {
  const navigate = useNavigate();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (next: boolean) => {
    onOpenChange?.(next);
    if (controlledOpen === undefined) setInternalOpen(next);
  };

  const go = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  if (asHelpIcon) {
    return (
      <>
        <Tooltip title="Delivery partner setup notes & flow">
          <IconButton
            color="primary"
            size="small"
            onClick={() => setOpen(true)}
            aria-label="Open delivery partner help notes"
            sx={{ border: 1, borderColor: 'divider' }}
          >
            <HelpOutline />
          </IconButton>
        </Tooltip>
        <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" scroll="paper">
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, pr: 1 }}>
            <Typography variant="h6" fontWeight={800} component="span">
              {DELIVERY_PARTNER_FLOW_META.title}
            </Typography>
            <IconButton size="small" onClick={() => setOpen(false)} aria-label="Close">
              <Close fontSize="small" />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <GuideBody compact={compact} onNavigate={go} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </>
    );
  }

  return (
    <Card elevation={0} sx={{ border: 1, borderColor: 'divider', borderRadius: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" fontWeight={800} gutterBottom>
          {DELIVERY_PARTNER_FLOW_META.title}
        </Typography>
        <GuideBody compact={compact} onNavigate={go} />
      </CardContent>
    </Card>
  );
}
